import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import responsiveStyles from "./responsiveStyles.module.scss";

import { Person, Review, Medication, ContactDetails } from "../../formParts";
import { Adverse } from "../../formParts/adverse";
import { Complete } from "../../formParts/complete";
import { SaveValueInterface } from "../../../interfaces";
import { fieldValidation, uploadDataToServer } from "../../../helpers";
import { useUpdateClaimData } from "../../../claimProvider";
import FloatingInfoBox from "../../reusable/FloatingInfoBox";
import ReasonsToSignUp from "../../pageComponents/reasonsToSignUp";
import WhatWereDoing from "../../pageComponents/whatWereDoing";
import WhoCanSignUp from "../../pageComponents/whoCanSignUp";
import NewsletterSignup from "../../pageComponents/newsletterSignup";
import PrivacyNotice from "../../pageComponents/privacyNotice";

function ClaimForm() {
  const [claimObject, setClaimObject] = useState<any>({});
  const [groupsComplete, setGroupsComplete] = useState(0);
  const [activeGroup, setActiveGroup] = useState("person");
  const [displayedPercentage, setDisplayedPercentage] = useState(0);
  const [actualPercentage, setActualPercentage] = useState(0);
  const [transitionClass, setTransitionClass] = useState<string>("");
  const [hideHeroTitle, setHideHeroTitle] = useState(false);

  const updateClaim = useUpdateClaimData();

  useEffect(() => {
    const totalSteps = 5; // person, contact, medication, adverse, review → complete
    let newPercentage = Math.round((groupsComplete / totalSteps) * 100);

    // Cap at 90% until the final step (complete)
    if (groupsComplete < 5) {
      newPercentage = Math.min(newPercentage, 90);
    }

    setActualPercentage(newPercentage);
  }, [groupsComplete]);

  useEffect(() => {
    if (displayedPercentage === actualPercentage) return;

    const isCountingUp = displayedPercentage < actualPercentage;

    const animationInterval = setInterval(() => {
      setDisplayedPercentage((prev) => {
        if (isCountingUp) {
          return prev < actualPercentage ? prev + 1 : actualPercentage;
        } else {
          return prev > actualPercentage ? prev - 1 : actualPercentage;
        }
      });
    }, 20);

    return () => clearInterval(animationInterval);
  }, [actualPercentage, displayedPercentage]);

  // Listen for sub-step changes in person form to hide hero title after step 1
  useEffect(() => {
    const handler = (e: any) => {
      const step = typeof e?.detail === "number" ? e.detail : 0;
      setHideHeroTitle(step > 0);
    };
    window.addEventListener("personStepChange", handler as any);
    return () => window.removeEventListener("personStepChange", handler as any);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) =>
    e.preventDefault();

  const saveFieldValue = (dataToSave: SaveValueInterface) => {
    console.log(
      "Saving field:",
      dataToSave.field,
      "with value:",
      dataToSave.value
    );
    setClaimObject((prevState: any) => ({
      ...prevState,
      [dataToSave.field]: dataToSave.value,
    }));

    const validated = fieldValidation(dataToSave);
    return validated;
  };

  const activeGroupAssignment = async (group: string) => {
    if (group === activeGroup) return;

    setTransitionClass("fadeOut");

    setTimeout(async () => {
      const flowOrder = [
        "person", // 1. Basic Personal Information
        "contact", // 2. Contact Details
        "medication", // 3. Medication Information
        "adverse", // 4. Adverse Effects
        "review", // 5. Review
        "complete",
      ];
      const nextIndex = flowOrder.indexOf(group);

      setGroupsComplete(nextIndex);

      if (group === "complete") {
        try {
          const claimData = await uploadDataToServer(claimObject);
          updateClaim(claimData);
          setActiveGroup(group);
        } catch (error) {
          console.error("Error uploading data:", error);
          setTransitionClass("");
          return;
        }
      } else {
        setActiveGroup(group);
        updateClaim({ ...claimObject });
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
      setTransitionClass("fadeIn");
      setTimeout(() => setTransitionClass(""), 200);
    }, 150);
  };

  return (
    <>
      <div
        className={`${styles.formContainer} ${responsiveStyles.formContainer}`}
      >
        <FloatingInfoBox
          message="complete in 90 seconds!"
          position="left"
          showBox={true}
        />
        <FloatingInfoBox
          message="we want to help YOU"
          position="right"
          showBox={true}
        />
        <div style={{ height: "40px" }}></div>

        <div className={`${styles.heroTitle} ${responsiveStyles.heroTitle}`}>
          <h1 className={`${styles.mainTitle} ${responsiveStyles.mainTitle}`}>
            <span className={styles.highlight}>
              Tell us about <span className={styles.yourHighlight}>YOUR</span>{" "}
              weight loss drug experience
            </span>
            <span
              className={`${styles.mobileBadge} ${responsiveStyles.mobileBadge}`}
            >
              Complete in
              <br />
              90 seconds!
            </span>
          </h1>
          <p className={`${styles.subtitle} ${responsiveStyles.subtitle}`}>
            Share your story regarding weight loss medications.
          </p>

          <div
            className={`${styles.titleDecoration} ${responsiveStyles.titleDecoration}`}
          ></div>
        </div>
        <div
          className={`${styles.progressContainer} ${responsiveStyles.progressContainer}`}
        >
          <div
            className={`${styles.progressBar} ${responsiveStyles.progressBar}`}
            style={{ width: `${displayedPercentage}%` }}
          ></div>
          <div
            className={`${styles.progressText} ${responsiveStyles.progressText}`}
            style={{
              left: `${Math.min(Math.max(displayedPercentage, 5), 95)}%`,
            }}
          >
            {displayedPercentage}%
          </div>
        </div>

        <form
          className={`${styles.form} ${responsiveStyles.form}`}
          onSubmit={(e) => handleSubmit(e)}
        >
          <div className={`${styles.formFields} ${styles[transitionClass]}`}>
            <Person
              saveValueFunct={saveFieldValue}
              isActive={activeGroup}
              assignGroup={activeGroupAssignment}
            />
            <ContactDetails
              saveValueFunct={saveFieldValue}
              isActive={activeGroup}
              assignGroup={activeGroupAssignment}
            />
            <Medication
              saveValueFunct={saveFieldValue}
              isActive={activeGroup}
              assignGroup={activeGroupAssignment}
            />
            <Adverse
              saveValueFunct={saveFieldValue}
              isActive={activeGroup}
              assignGroup={activeGroupAssignment}
            />
            <Review
              saveValueFunct={saveFieldValue}
              isActive={activeGroup}
              assignGroup={activeGroupAssignment}
            />
            <Complete
              saveValueFunct={saveFieldValue}
              isActive={activeGroup}
              assignGroup={activeGroupAssignment}
            />
          </div>
        </form>
      </div>

      <PrivacyNotice />

      <ReasonsToSignUp />
      <WhatWereDoing />
      <WhoCanSignUp />
      <NewsletterSignup />
    </>
  );
}

export default ClaimForm;
